import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../config/axios';
import Model3DViewer from '../components/Model3DViewer';
import ARViewer from '../components/ARViewer';
import Toast from '../components/Toast';
import { getS3Url } from '../config/s3Config';
import './Model3DViewerPage.css';

const TEXTURES = [
  { 
    name: 'Plywood', 
    folder: 'plywood', 
    baseColor: getS3Url('textures/plywood/basecolor.jpg'),
    woodType: 'Plywood',
    finish: 'Plain',
    price: 0
  },
  { 
    name: 'Dark Wood', 
    folder: 'dark_wood', 
    baseColor: getS3Url('textures/dark_wood/basecolor.jpg'),
    woodType: 'Dark Wood',
    finish: 'Varnished',
    price: 2000
  },
  { 
    name: 'Oak Veneer', 
    folder: 'oak_veener', 
    baseColor: getS3Url('textures/oak_veener/basecolor.jpg'),
    woodType: 'Oak Veneer',
    finish: 'Plain',
    price: 2500
  },
  { 
    name: 'Plywood Varnished', 
    folder: 'plywood_varnished', 
    baseColor: getS3Url('textures/plywood_varnished/basecolor.jpg'),
    woodType: 'Plywood',
    finish: 'Varnished',
    price: 2000
  },
];

const Model3DViewerPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('3d');
  const [quantity, setQuantity] = useState(1);
  const [currentWoodType, setCurrentWoodType] = useState('Plywood');
  const [currentFinish, setCurrentFinish] = useState('Plain');
  const [selectedTexture, setSelectedTexture] = useState(TEXTURES[0]);
  const [currentModelIndex, setCurrentModelIndex] = useState(0);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Calculate dynamic price based on selected model variant and texture
  const calculateTotalPrice = () => {
    if (!product) return 0;
    
    // Get the base price from the selected model variant or default product price
    let basePrice = product.price;
    if (product.models && product.models.length > 0 && product.models[currentModelIndex]) {
      basePrice = product.models[currentModelIndex].price;
    }
    
    // Add texture price
    const texturePrice = selectedTexture.price || 0;
    
    return basePrice + texturePrice;
  };

  const totalPrice = calculateTotalPrice();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await api.get(`/api/products/${id}`);
        setProduct(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching product:', error);
        setLoading(false);
      }
    };
    
    fetchProduct();
  }, [id]);

  const handleScrollToFooter = () => {
    const footerContent = document.querySelector('.footer-content');
    if (footerContent) {
      footerContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleAddToCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    
    // Get selected model variant info
    const selectedModel = product.models && product.models.length > 0 
      ? product.models[currentModelIndex] 
      : null;
    const variantName = selectedModel ? selectedModel.variantName : '';
    
    // Create a unique cart item identifier including variant and texture
    const cartItemKey = `${id}_${currentModelIndex}_${selectedTexture.folder}`;
    
    // Find existing item with same product, variant, and texture
    const existingItem = cart.find(item => 
      item.productId === id && 
      item.modelIndex === currentModelIndex &&
      item.textureFolder === selectedTexture.folder
    );
    
    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      cart.push({
        productId: id,
        name: product.name + (variantName ? ` - ${variantName}` : ''),
        price: totalPrice,
        basePrice: selectedModel ? selectedModel.price : product.price,
        texturePrice: selectedTexture.price,
        quantity: quantity,
        image: product.image,
        modelIndex: currentModelIndex,
        variantName: variantName,
        textureName: selectedTexture.name,
        textureFolder: selectedTexture.folder,
        woodType: currentWoodType,
        finish: currentFinish,
      });
    }
    
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    showToast(`${quantity}x ${product.name}${variantName ? ' - ' + variantName : ''} (${selectedTexture.name}) added to cart! Price: ₱${totalPrice.toLocaleString()}`, 'success');
  };

  const handleCheckout = () => {
    handleAddToCart();
    navigate('/checkout');
  };

  const handleClose = () => {
    navigate(-1);
  };

  const handleTextureChange = (texture) => {
    setSelectedTexture(texture);
    setCurrentWoodType(texture.woodType);
    setCurrentFinish(texture.finish);
  };

  const handleMobileTextureChange = (texture) => {
    handleTextureChange(texture);
  };

  if (loading) {
    return (
      <div className="model-viewer-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading 3D Model...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="model-viewer-page">
        <div className="error-container">
          <p>Product not found</p>
          <button onClick={handleClose} className="btn-back">Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="model-viewer-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Header */}
      <div className="viewer-header">
        <div className="header-content">
          <div className="header-left">
            <button onClick={handleClose} className="btn-back-header">
              ← Back
            </button>
            <div className="header-divider"></div>
            <div className="product-header-info">
              <h2>{product.name}</h2>
              <p>{product.category}</p>
            </div>
          </div>
          <button onClick={handleClose} className="btn-close">✕</button>
        </div>
      </div>

      {/* 3D Viewer Content */}
      <div className="viewer-content">
        {/* 3D Model Display */}
        <div className="model-display">
          {viewMode === '3d' ? (
            // 3D View Mode
            product.models && product.models.length > 0 ? (
              <Model3DViewer 
                models={product.models}
                className="fullscreen-viewer"
                onTextureChange={handleTextureChange}
                selectedModelIndex={currentModelIndex}
                onModelChange={setCurrentModelIndex}
                selectedTexture={selectedTexture}
              />
            ) : (
              <div className="no-model-placeholder">
                <div className="placeholder-icon">📦</div>
                <h3>3D Model View</h3>
                <p>Interactive 3D model of {product.name}</p>
                <small>3D model coming soon</small>
              </div>
            )
          ) : (
            // AR View Mode
            product.models && product.models.length > 0 ? (
              <ARViewer 
                models={product.models}
                selectedModelIndex={currentModelIndex}
                selectedTexture={selectedTexture}
              />
            ) : (
              <div className="no-model-placeholder">
                <div className="placeholder-icon">🎯</div>
                <h3>AR View</h3>
                <p>AR experience for {product.name}</p>
                <small>3D model required for AR</small>
              </div>
            )
          )}
          
          {/* Controls Info - Only show in 3D mode */}
          {viewMode === '3d' && (
            <div className="controls-info">
              <span className="controls-icon">🔍</span>
              Drag to rotate • Scroll to zoom
            </div>
          )}

          {/* Floating Scroll Down Button (Mobile Only) */}
          <button 
            className="floating-scroll-down" 
            onClick={handleScrollToFooter}
            aria-label="Scroll to options"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          
          {/* AR Instructions - Only show in AR mode */}
          {/* {viewMode === 'ar' && product.models && product.models.length > 0 && (
            <div className="ar-info-overlay">
              <div className="ar-info-content">
                <p>📱 Tap "Enter AR" to start the experience</p>
                <small>Point your camera at a flat surface and tap to place furniture</small>
              </div>
            </div>
          )} */}
        </div>

        {/* Side Info Panel (Desktop) */}
        <div className="side-info-panel">
          <h3>Product Details</h3>
          <div className="info-section">
            <div className="info-item">
              <p className="info-label">Price</p>
              <p className="info-value price">₱{totalPrice.toLocaleString()}</p>
            </div>
            <div className="info-item">
              <p className="info-label">Dimensions</p>
              <p className="info-value">180 × 75 × 90 cm</p>
            </div>
            <div className="info-item">
              <p className="info-label">Wood Type</p>
              <p className="info-value">{currentWoodType}</p>
            </div>
            <div className="info-item">
              <p className="info-label">Finish</p>
              <p className="info-value">{currentFinish}</p>
            </div>
            <div className="info-item">
              <p className="info-label">Stock</p>
              <p className="info-value">{product.stock > 0 ? 'In Stock' : 'Out of Stock'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="viewer-footer">
        <div className="footer-content">
          
        {/* Mobile Selectors */}
        <div className="mobile-selectors">
          {/* Model Selector */}
          {product.models && product.models.length > 1 && (
            <div className="mobile-model-selector">
              <label>Model Variant:</label>
              <select
                value={currentModelIndex}
                onChange={(e) => setCurrentModelIndex(parseInt(e.target.value))}
              >
                {product.models.map((model, index) => (
                  <option key={index} value={index}>
                    {model.variantName || `Model ${index + 1}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Texture Selector */}
          <div className="mobile-texture-selector">
            <p className="mobile-texture-selector-label">Wood Texture:</p>
            <div className="mobile-texture-options-wrapper">
              <div className="mobile-texture-options">
                {TEXTURES.map((texture, index) => (
                  <div
                    key={index}
                    className={`mobile-texture-option ${selectedTexture.folder === texture.folder ? 'selected' : ''}`}
                    onClick={() => handleMobileTextureChange(texture)}
                  >
                    <div className="mobile-texture-image-wrapper">
                      <img
                        src={texture.baseColor}
                        alt={texture.name}
                        className="mobile-texture-image"
                      />
                    </div>
                    <span className="mobile-texture-name">{texture.name}</span>
                    <span className="mobile-texture-price">
                      {texture.price === 0 ? 'Free' : `+₱${texture.price.toLocaleString()}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
          {/* View Mode Toggle */}
          <div className="view-mode-toggle">
            <button 
              className={`mode-btn ${viewMode === '3d' ? 'active' : ''}`}
              onClick={() => setViewMode('3d')}
            >
              📦 3D View
            </button>
            <button 
              className={`mode-btn ${viewMode === 'ar' ? 'active' : ''}`}
              onClick={() => setViewMode('ar')}
            >
              🎯 AR View
            </button>
          </div>

          {/* Quantity Selector */}
          <div className="quantity-section">
            <span className="qty-label">Quantity:</span>
            <div className="qty-selector">
              <button 
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="qty-btn"
              >
                -
              </button>
              <span className="qty-value">{quantity}</span>
              <button 
                onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                className="qty-btn"
              >
                +
              </button>
            </div>
          </div>

          <div className="spacer"></div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button className="btn-favorite">
              ❤️ <span className="btn-text">Favorites</span>
            </button>
            <button onClick={handleAddToCart} className="btn-add-cart">
              🛒 Add to Cart
            </button>
            <button onClick={handleCheckout} className="btn-checkout">
              💳 Checkout
            </button>
          </div>
        </div>

        {/* Mobile Product Info */}
        <div className="mobile-info">
          <div className="mobile-info-left">
            <p className="mobile-label">Price</p>
            <p className="mobile-price">₱{totalPrice.toLocaleString()}</p>
          </div>
          <div className="mobile-info-right">
            <p className="mobile-label">{currentWoodType} Wood</p>
            <p className="mobile-text">{currentFinish}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Model3DViewerPage;
